import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";

const Submitted = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/diagnostics/${id}/summary`)
      .then((r) => setSummary(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <div className="min-h-screen bg-[#050608] text-white">
      <header className="border-b border-[#1C1F26]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Logo />
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="text-[#9aa3ad] hover:text-white hover:bg-[#1C1F26]"
            data-testid="back-home-btn"
          >
            <ArrowLeft size={16} className="mr-2" /> Início
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="pop-in text-center mb-10">
          <div
            className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-5"
            style={{ background: "rgba(34,182,115,0.15)" }}
          >
            <CheckCircle2 size={42} className="text-[#22B673]" />
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#22B673]/10 border border-[#22B673]/40 text-[#22B673] text-xs uppercase tracking-[0.28em] mb-4">
            <Sparkles size={14} /> Diagnóstico enviado com sucesso
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
            Obrigado pela sua participação!
          </h1>
          <p className="mt-3 text-[#9aa3ad] max-w-xl mx-auto leading-relaxed">
            Seu diagnóstico foi recebido pela equipe UniGR. Em breve um(a) consultor(a) entrará
            em contato para apresentar o resultado completo, plano de ação e recomendações
            personalizadas para sua operação.
          </p>
        </div>

        {loading ? (
          <div className="text-center text-[#6B8DB3] py-10">
            <Loader2 className="animate-spin inline mr-2" /> Carregando resumo...
          </div>
        ) : summary ? (
          <div className="p-7 rounded-2xl border border-[#3A3F47] bg-[#1C1F26]">
            <div className="flex items-baseline justify-between mb-5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.32em] text-[#6B8DB3] mb-1">
                  Pré-visualização do resumo
                </div>
                <h2 className="text-xl font-semibold">Resultado por Sessão</h2>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-white">
                  {Math.round(summary.total)}%
                </div>
                <div className="text-xs text-[#6B8DB3]">Maturidade global</div>
              </div>
            </div>

            <div className="space-y-3">
              {summary.sections.map((s) => (
                <div key={s.section_id}>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="text-white">{s.name}</span>
                    <span className="font-mono text-xs">
                      <span style={{ color: s.color }}>{Math.round(s.score)}%</span>
                      <span className="text-[#6B8DB3] ml-2">· {s.level}</span>
                    </span>
                  </div>
                  <div className="h-2 bg-[#0e1015] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${s.score}%`, background: s.color }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="mt-8 text-center text-xs text-[#6B8DB3]">
          ID do protocolo: <span className="font-mono text-white">{id}</span>
        </div>
      </main>
    </div>
  );
};

export default Submitted;
